import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const MODES = [
  {
    key: 'direct',
    label: { en: 'Direct Conversion', ar: 'تحويل مباشر', fa: 'تبدیل مستقیم' },
    desc:  { en: 'Preserves slides layout and design exactly as-is', ar: 'يحافظ على تخطيط الشرائح وتصميمها كما هو', fa: 'چیدمان و طراحی اسلایدها را دقیقاً حفظ می‌کند' },
  },
  {
    key: 'reflow',
    label: { en: 'Content Reflow', ar: 'إعادة تدفق المحتوى', fa: 'بازآرایی محتوا' },
    desc:  { en: 'Extracts text and images as a readable document — slide titles become headings', ar: 'يستخرج النصوص والصور كمستند قابل للقراءة — عناوين الشرائح تصبح عناوين المستند', fa: 'متن و تصاویر را به صورت سند خوانا استخراج می‌کند — عنوان اسلایدها به heading تبدیل می‌شوند' },
  },
];

export default function PptxToPdfCard({ lang }) {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('direct');

  const onDrop = useCallback((accepted) => {
    const valid = accepted.filter(f => /\.(ppt|pptx)$/i.test(f.name));
    if (valid.length < accepted.length) {
      toast.error(
        lang === 'ar' ? 'يُسمح فقط بملفات .ppt / .pptx'
        : lang === 'fa' ? 'فقط فایل‌های .ppt / .pptx مجاز است'
        : 'Only .ppt / .pptx files are allowed'
      );
    }
    setFiles(prev => {
      const combined = [...prev, ...valid];
      if (combined.length > 10) {
        toast.error(lang === 'ar' ? 'الحد الأقصى 10 ملفات' : lang === 'fa' ? 'حداکثر ۱۰ فایل' : 'Maximum 10 files');
        return combined.slice(0, 10);
      }
      return combined;
    });
  }, [lang]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    },
    multiple: true,
  });

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setFiles(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setFiles(prev => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleConvert = async () => {
    if (files.length < 1) {
      toast.error(lang === 'ar' ? 'اختر ملفاً واحداً على الأقل' : lang === 'fa' ? 'حداقل یک فایل انتخاب کنید' : 'Select at least one file');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('mode', mode);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`${API}/api/convert/pptx-to-pdf`, formData, {
        responseType: 'blob',
        headers,
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'output.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(
        lang === 'ar' ? 'تم التحويل بنجاح! 🎉' : lang === 'fa' ? 'تبدیل موفقیت‌آمیز بود! 🎉' : 'Converted successfully! 🎉',
        { style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' } }
      );
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل التحويل' : lang === 'fa' ? 'تبدیل ناموفق بود' : 'Conversion failed';
      if (err.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); msg = JSON.parse(text).detail || msg; } catch { }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const label = {
    drop: lang === 'ar' ? 'اسحب ملفات .ppt/.pptx هنا أو انقر للاختيار' : lang === 'fa' ? 'فایل‌های .ppt/.pptx را اینجا بکشید یا کلیک کنید' : 'Drag .ppt/.pptx files here or click to select',
    order: lang === 'ar' ? 'ترتيب الملفات' : lang === 'fa' ? 'ترتیب فایل‌ها' : 'File order',
    convert: lang === 'ar' ? '⚡ تحويل الآن' : lang === 'fa' ? '⚡ تبدیل کن' : '⚡ Convert Now',
    converting: lang === 'ar' ? 'جاري التحويل...' : lang === 'fa' ? 'در حال تبدیل...' : 'Converting...',
    clear: lang === 'ar' ? 'مسح الكل' : lang === 'fa' ? 'پاک کردن' : 'Clear All',
    modeLabel: lang === 'ar' ? 'نمط التحويل' : lang === 'fa' ? 'حالت تبدیل' : 'Conversion Mode',
  };

  return (
    <div>
      {/* Mode picker */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
        }}>
          {label.modeLabel}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {MODES.map(m => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              style={{
                background: mode === m.key ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${mode === m.key ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                padding: '10px 14px',
                textAlign: lang === 'ar' || lang === 'fa' ? 'right' : 'left',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                fontSize: '13px', fontWeight: 600,
                color: mode === m.key ? '#fde68a' : 'rgba(255,255,255,0.75)',
              }}>
                {m.label[lang] || m.label.en}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>
                {m.desc[lang] || m.desc.en}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? '#f59e0b' : 'rgba(255,255,255,0.15)'}`,
          borderRadius: '12px',
          padding: '32px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? 'rgba(245,158,11,0.06)' : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
          marginBottom: '16px',
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖥️</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{label.drop}</div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>
          .ppt / .pptx — {lang === 'ar' ? 'حتى 10 ملفات' : lang === 'fa' ? 'تا ۱۰ فایل' : 'up to 10 files'}
        </div>
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
          }}>
            {label.order} ({files.length})
          </div>
          {files.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
              padding: '8px 12px', marginBottom: '6px',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', minWidth: '20px' }}>
                {i + 1}.
              </span>
              <span style={{ flex: 1, fontSize: '13px', color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                {(f.size / 1024).toFixed(0)} KB
              </span>
              <button onClick={() => moveUp(i)} disabled={i === 0} style={arrowBtn}>▲</button>
              <button onClick={() => moveDown(i)} disabled={i === files.length - 1} style={arrowBtn}>▼</button>
              <button onClick={() => removeFile(i)} style={{ ...arrowBtn, color: '#e94560' }}>✕</button>
            </div>
          ))}
          <button
            onClick={() => setFiles([])}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              fontSize: '12px', cursor: 'pointer', padding: '4px 0',
            }}
          >
            {label.clear}
          </button>
        </div>
      )}

      <button
        className="btn-primary"
        onClick={handleConvert}
        disabled={files.length < 1 || loading}
        style={{ opacity: files.length < 1 ? 0.5 : 1 }}
      >
        {loading ? <><span className="spinner" />{label.converting}</> : label.convert}
      </button>
    </div>
  );
}

const arrowBtn = {
  background: 'none',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '4px',
  color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '11px',
  lineHeight: 1.4,
};
